import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BirdEventRecord, BirdEventType, IsoDate, NumericString } from '@henlytics/types'
import { api } from '../lib/api-client'
import { queryKeys } from '../lib/query-keys'
import { clientUuid } from '../lib/uuid'

export function useBirdEvents(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.birdEvents(batchId ?? ''),
    queryFn: ({ signal }) => api.get<BirdEventRecord[]>(`/bird-events?batchId=${batchId}`, signal),
    enabled: Boolean(batchId),
  })
}

export interface NewBirdEvent {
  batchId: string
  eventDate: IsoDate
  eventType: BirdEventType
  /** Unsigned. The API derives the sign from the event type. */
  qty: number
  reason?: string | null
  salvageAmount?: NumericString | null
  notes?: string | null
  clientUuid?: string
}

export function useCreateBirdEvent(batchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NewBirdEvent) =>
      api.post<BirdEventRecord>('/bird-events', {
        ...input,
        clientUuid: input.clientUuid ?? clientUuid(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.birdEvents(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}

/**
 * Corrections are void-and-replace, never an edit (§9 rule 1). There is no
 * update mutation on this hook on purpose.
 */
export function useVoidBirdEvent(batchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, replacement }: { eventId: string; replacement?: NewBirdEvent }) =>
      api.post<{ voided: BirdEventRecord; replacement: BirdEventRecord | null }>(
        `/bird-events/${eventId}/void`,
        replacement
          ? { replacement: { ...replacement, clientUuid: replacement.clientUuid ?? clientUuid() } }
          : {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.birdEvents(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}
